import { useCallback, useEffect, useRef, useState } from 'react';
import type { MeetingDetail, MeetingEvent, MeetingProgress } from '@shared/contract';
import { api } from '../lib/api';

export type Connection = 'connecting' | 'open' | 'reconnecting';

/** Hämtar mötet och håller det uppdaterat via SSE (/api/meetings/:id/events). */
export function useMeeting(id: string) {
  const [detail, setDetail] = useState<MeetingDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connection, setConnection] = useState<Connection>('connecting');
  const [lastEvent, setLastEvent] = useState<MeetingEvent | null>(null);
  const [deleted, setDeleted] = useState(false);
  const refetchTimer = useRef<number | null>(null);

  const refetch = useCallback(async () => {
    try {
      setDetail(await api.getMeeting(id));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [id]);

  const scheduleRefetch = useCallback(() => {
    if (refetchTimer.current !== null) return;
    refetchTimer.current = window.setTimeout(() => {
      refetchTimer.current = null;
      void refetch();
    }, 250);
  }, [refetch]);

  useEffect(() => {
    void refetch();
    const es = new EventSource(`/api/meetings/${id}/events`, { withCredentials: true });
    es.onopen = () => {
      setConnection('open');
      // Hämta allt på nytt efter återanslutning så att inga händelser missas.
      scheduleRefetch();
    };
    es.onerror = () => setConnection('reconnecting');
    const handle = (msg: MessageEvent) => {
      let event: MeetingEvent;
      try {
        event = JSON.parse(msg.data);
      } catch {
        return;
      }
      setLastEvent(event);
      if (event.type === 'meeting.deleted') {
        setDeleted(true);
        es.close();
        return;
      }
      if (event.type === 'progress.updated' && event.data?.progress) {
        const progress = event.data.progress as MeetingProgress;
        setDetail((d) => (d ? { ...d, progress } : d));
        return;
      }
      if (event.type === 'segment.received') return;
      scheduleRefetch();
    };
    const types = [
      'meeting.started', 'meeting.paused', 'meeting.resumed', 'meeting.finishing', 'meeting.finished',
      'meeting.deleted', 'transcript.updated', 'summary.requested', 'summary.ready', 'summary.failed',
      'question.requested', 'answer.ready', 'answer.failed', 'progress.updated', 'segment.received',
      'visualization.requested', 'visualization.ready', 'visualization.failed',
    ];
    types.forEach((t) => es.addEventListener(t, handle as EventListener));
    return () => {
      es.close();
      if (refetchTimer.current !== null) clearTimeout(refetchTimer.current);
    };
  }, [id, refetch, scheduleRefetch]);

  return { detail, setDetail, error, connection, lastEvent, deleted, refetch };
}
